<template>
  <div v-if="getBet.odds.length > 0" class="actions-american" :class="{ 'actions-american--inline': inlineMode }">

    <!-- Inline alerts (hidden in inline/bar mode) -->
    <template v-if="!inlineMode">
      <div v-if="warningOddsChanged" class="il-alert il-alert--warn">{{ $t('updateOddsCart') }}</div>
      <div v-if="showAlertLogin" class="il-alert il-alert--err">{{ $t('alertlogin') }}</div>
      <div v-if="cartWarnings.length > 0" class="il-alert il-alert--err">
        <span v-for="(warn, i) in cartWarnings" :key="i">
          {{ warn.messageKey ? $t(warn.messageKey, warn.messageParams || {}) : $t(warn.message) }}
        </span>
      </div>
    </template>

    <!-- Place bet / Confirm changes — full-width primary button -->
    <div class="il-btn-wrap">
      <button
        class="il-place"
        :class="{ 'il-place--disabled': shouldDisable }"
        :disabled="shouldDisable"
        @click="saveBet()"
      >
        <!-- Loading spinner -->
        <div v-if="getIsCartUpdating || placing" class="il-place__center">
          <svg class="il-spin" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:.25"/>
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
        </div>

        <!-- Normal: amount + winning -->
        <div v-else class="il-place__center il-place__hover">
          <span class="il-place__row1">{{ $t('bet') }} {{ getSettings.currencySymbol }} {{ displayBetAmount | round_digits(2) }}</span>
          <span v-if="displayWinning > 0" class="il-place__row2">{{ $t('winning') }}: {{ getSettings.currencySymbol }} {{ displayWinning | round_digits(2) }}</span>
        </div>
      </button>

      <!-- Book bet — text row below, prematch only, hidden in inline mode -->
      <button
        v-if="!inlineMode && !isBookDisabled && (getBet.context === 'prematch' || getBet.context === 'antepost')"
        class="il-book"
        @click="bookingBet"
      >
        <div v-if="getIsCartUpdating" class="il-place__center">
          <svg class="il-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:.25"/>
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
        </div>
        <span v-else class="il-book__label">{{ $t('bookBet') }}</span>
      </button>
    </div>

    <!-- Error toast (fixed, outside the bar) -->
    <transition name="toast-slide">
      <div v-if="dialogError" class="error-toast">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#ef4444" viewBox="0 0 16 16">
          <path d="M4.54.146A.5.5 0 0 1 4.893 0h6.214a.5.5 0 0 1 .353.146l4.394 4.394a.5.5 0 0 1 .146.353v6.214a.5.5 0 0 1-.146.353l-4.394 4.394a.5.5 0 0 1-.353.146H4.893a.5.5 0 0 1-.353-.146L.146 11.46A.5.5 0 0 1 0 11.107V4.893a.5.5 0 0 1 .146-.353zM5.1 1 1 5.1v5.8L5.1 15h5.8l4.1-4.1V5.1L10.9 1z"/>
          <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
        </svg>
        <div class="error-toast__content">
          <span class="error-toast__code">{{ error_code }}</span>
          <span class="error-toast__reason">{{ error_reason }}</span>
        </div>
        <button class="error-toast__close" @click="closeError">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
          </svg>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { EventBus } from '../../plugins/eventBus';
import { resolveErrorDisplay } from '@/services/placeBetErrorDisplay';

export default {
  name: 'ActionsAmerican',
  props: {
    inlineMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogError: false,
      error_code: '',
      error_reason: '',
      placing: false,
      showAlertLogin: false,
      warningOddsChanged: false,
      attemptsMade: 0,
      MAX_ATTEMPTS: 3,
      _errorTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      getBet:             'cart/getBet',
      getAuthenticated:   'user/getAuthenticated',
      getCanPlay:         'user/getCanPlay',
      getLanguage:        'settings/getLanguage',
      getOddsVariation:   'cart/getOddsVariation',
      getSettings:        'cart/getSettings',
      getIsCartUpdating:  'cart/getIsCartUpdating',
      getPlatformSettings:'settings/getPlatformSettings',
      getShortCode:       'settings/getShortCode',
      getOddsType:        'odds/getOddsType',
      getType:            'user/getType',
      getId:              'user/getId',
      getModalIdle:       'user/getModalIdle',
      getAcceptingBets:   'cart/getAcceptingBets',
      getAcceptedBetWithChange: 'cart/getAcceptedBetWithChange',
      getParentStakeLock: 'cart/getParentStakeLock',
    }),
    // Bet sent to operator review (or with a pending change proposal):
    // the slip is locked until the review is resolved.
    isUnderReview() {
      return (this.getAcceptingBets || []).length > 0 || (this.getAcceptedBetWithChange || []).length > 0;
    },
    hasBetErrors() {
      return !this.getBet.check && this.getBet.errors && this.getBet.errors.length > 0;
    },
    hasBBRedundant() {
      return (this.getBet.odds || []).some(o => o.bb_redundant === true || o.bb_unavailable === true);
    },
    shouldShowConfirm() {
      return this.getBet.odds.some(o => o.changed);
    },
    isPlaceDisabled() {
      // getParentStakeLock: the embedding page has just set the stake, the player sees it first
      return this.hasBetErrors || this.hasBBRedundant || this.placing || this.getIsCartUpdating === true || this.isUnderReview
          || this.getParentStakeLock;
    },
    isBookDisabled() {
      const ctx = this.getBet.context;
      return this.hasBetErrors || (ctx !== 'prematch' && ctx !== 'antepost');
    },
    shouldDisable() {
      return this.isPlaceDisabled;
    },
    cartWarnings() {
      if (!this.getBet || !this.getBet.errors || !this.getBet.errors.length) return [];
      const inline = ['MULTIPLICITY_TOO_LOW', 'MULTIPLICITY_TOO_HIGH', 'MULTIPLICITY_CONFLICT', 'ODD_DISABLED', 'CHANGED_ODD_VALUE'];
      return this.getBet.errors.filter(e => inline.includes(e.type));
    },
    displayBetAmount() {
      const b = this.getBet;
      if (b.type === 'single' || b.type === 'multiple') return +b._multiple_detail.amount || 0;
      if (b.type === 'integral') return +b._integral_detail.amount || 0;
      if (b.type === 'system') return +b._system_detail.amount_system || 0;
      return 0;
    },
    displayWinning() {
      const b = this.getBet;
      if (b.type === 'single' || b.type === 'multiple') return +b._multiple_detail.winning || 0;
      if (b.type === 'integral') return +b._integral_detail.winning_max || 0;
      if (b.type === 'system') return +b._system_detail.winning_max || 0;
      return 0;
    },
  },
  methods: {
    ...mapActions({
      resetCart:            'cart/resetCart',
      placeBet:             'cart/placeBet',
      bookBet:              'cart/bookBet',
      replaceOdds:          'cart/replaceOdds',
      acceptAllOdds:        'cart/acceptAllOdds',
      refreshCart:          'cart/refreshCart',
      setLoadingCart:       'cart/setLoadingCart',
      addBetInAcceptance:   'cart/addBetInAcceptance',
      markOddDisabled:      'cart/markOddDisabled',
      markOddChanged:       'cart/markOddChanged',
      updateOddMultiplicity:'cart/updateOddMultiplicity',
      applyOddValueChanges: 'cart/applyOddValueChanges',
      setConfirmedBet:      'cart/setConfirmedBet',
      removeOddFromCart:    'cart/removeOddFromCart',
      markOddSuspended:      'cart/markOddSuspended',
      clearOddsSuspended:    'cart/clearOddsSuspended',
    }),

    async saveBet() {
      if (this.placing || this.getIsCartUpdating || this.isUnderReview || this.getParentStakeLock) return;
      if (!await this._beginFlow()) return;

      this.placing = true;
      let response;
      try {
        response = await this.placeBet({
          betContext: this.getBet.context,
          lang: this.getLanguage,
          accept_odds: this.getOddsVariation,
        });
      } catch (e) {
        this.placing = false;
        await this.setLoadingCart({ status: false });
        this._showError(this.$t('errors.systemError'), this.$t('pleaseTryAgain'));
        return;
      }

      this.placing = false;
      await this.setLoadingCart({ status: false });

      if (response && response.result === true) {
        this.attemptsMade = 0;
        await this._onSuccess(response);
      } else {
        await this._onError(response, this.saveBet);
      }
    },

    async bookingBet() {
      const ctx = this.getBet.context;
      if ((ctx !== 'prematch' && ctx !== 'antepost') || this.getIsCartUpdating) return;
      if (!await this._beginFlow()) return;

      let response;
      try {
        response = await this.bookBet({ betContext: ctx, lang: this.getLanguage });
      } catch (e) {
        await this.setLoadingCart({ status: false });
        this._showError(this.$t('errors.systemError'), this.$t('pleaseTryAgain'));
        return;
      }

      await this.setLoadingCart({ status: false });

      if (response && response.result === true) {
        this.attemptsMade = 0;
        const betCode = response.bet_code || response.shortCode || '';
        await this.setConfirmedBet({ bet_code: betCode, is_booked: true });
        this.dialogError = false;
      } else {
        await this._onError(response, this.bookingBet);
      }
    },

    async confirmChanges() {
      await this.acceptAllOdds();
      await this.refreshCart();
    },

    async _beginFlow() {
      await this.setLoadingCart({ status: true });
      if (this.getAuthenticated === false) {
        await this.setLoadingCart({ status: false });
        this.showAlertLogin = true;
        window.parent.postMessage({ type: 'OPEN_LOGIN' }, '*');
        return false;
      }
      if (this.hasSuspendedOdds) {
        await this.clearOddsSuspended();
      }

      if (this.hasBetErrors) {
        await this.setLoadingCart({ status: false });
        return false;
      }
      return true;
    },

    async _onSuccess(response) {
      const betCode = this.getShortCode ? response.shortCode : response.bet_code;
      this.warningOddsChanged = false;
      this.dialogError = false;

      const changedOdds = (response.warnings || [])
        .filter(w => w.code === 'CHANGED_ODD_VALUE')
        .map(w => ({ outcomeId: w.outcomeId, newOdd: w.newOdd, oldOdd: w.odd }));

      let oldTotals = null;
      if (changedOdds.length > 0) {
        const b = this.getBet;
        const t = b.type;
        if (t === 'single' || t === 'multiple') {
          oldTotals = { odds_total: b._multiple_detail.odds_total, winning_total: b._multiple_detail.winning_total };
        } else if (t === 'integral') {
          oldTotals = { odds_total: b._integral_detail.odds_total_max, winning_total: b._integral_detail.winning_total_max };
        } else if (t === 'system') {
          oldTotals = { odds_total: 0, winning_total: b._system_detail.winning_total_max };
        }
        await this.applyOddValueChanges(changedOdds);
        await this.refreshCart();
      }

      const b = this.getBet;
      const type = b.type;
      let amount = 0, oddsTotal = 0, winning = 0, winningTotal = 0;
      if (type === 'single' || type === 'multiple') {
        amount = b._multiple_detail.amount;
        oddsTotal = b._multiple_detail.odds_total;
        winning = b._multiple_detail.winning;
        winningTotal = b._multiple_detail.winning_total;
      } else if (type === 'integral') {
        amount = b._integral_detail.amount_total;
        oddsTotal = b._integral_detail.odds_total_max;
        winning = b._integral_detail.winning_max;
        winningTotal = b._integral_detail.winning_total_max;
      } else if (type === 'system') {
        amount = b._system_detail.amount_system;
        oddsTotal = 0;
        winning = b._system_detail.winning_max;
        winningTotal = b._system_detail.winning_total_max;
      }

      await this.setConfirmedBet({
        bet_code:          betCode,
        is_booked:         false,
        type,
        amount,
        odds_total:        oddsTotal,
        old_odds_total:    oldTotals ? oldTotals.odds_total : null,
        winning,
        winning_total:     winningTotal,
        old_winning_total: oldTotals ? oldTotals.winning_total : null,
        bonus:             b._multiple_detail.bonus,
        bonus_percentage:  b._multiple_detail.bonus_percentage,
        odds:              JSON.parse(JSON.stringify(b.odds)),
      });
    },

    async _onError(response, retryFn) {
      const errorCode = response && response.error_code;

      if (errorCode === 'update_odds') {
        await this._handleUpdateOdds(response, retryFn);
        return;
      }

      const apiErrors = (response && response.data && response.data.errors) || [];
      if (apiErrors.length > 0) {
        await this._syncCartErrors(apiErrors);
      }

      if (errorCode === 'review' && response.data) {
        const data = response.data;
        const code = typeof data === 'object' ? (data.code || data.bet_code || data.betCode) : data;
        // Full snapshot: on accept the socket turns it into the confirmed bet
        if (code) await this.addBetInAcceptance(this._buildAcceptancePayload(code));
        return;
      }

      const display = resolveErrorDisplay(this, response);
      this._showError(display.code, display.reason);
    },

    async _handleUpdateOdds(response, retryFn) {
      const dataToUpdate = (response && response.data_to_update) || [];
      await this.replaceOdds({ odds: dataToUpdate });
      this.$root.$emit('forceUpdateOddsSummary');

      if (!this.getOddsVariation) return;

      this.warningOddsChanged = true;
      await this.acceptAllOdds();
      await this.refreshCart();

      const hasBlocker = dataToUpdate.some(o => o.reason === 'disabled' || o.reason === 'time_over');
      if (hasBlocker) {
        this._showError(this.$t('bet_not_accepted'), this.$t('oddNotAvailableInCart'));
        return;
      }

      if (++this.attemptsMade <= this.MAX_ATTEMPTS) {
        await retryFn();
      } else {
        this.warningOddsChanged = true;
        this._showError(this.$t('bet_not_accepted'), this.$t('pleaseTryAgain'));
      }
    },

    async _syncCartErrors(errors) {
      let needsRefresh = false;
      for (const err of errors) {
        if ((err.code === 'ODD_DISABLED' || err.code === 'ODD_INVALID_STATUS' || err.code === 'UNSUPPORTED_LIVE_EVENT' || err.code === 'time_over' || err.code === 'last_update_over' || err.code === 'invalid_odd') && err.outcomeId) {
          await this.markOddDisabled({ outcomeId: err.outcomeId });
          needsRefresh = true;
        } else if (err.code === 'ODD_SUSPENDED' && err.outcomeId) {
          // Stato transitorio: la quota può tornare giocabile a breve.
          // Non blocchiamo la scommessa (nessun push a bet.errors): l'utente vede
          // l'avviso inline + il badge sulla riga, e può ritentare il piazzamento
          // quando vuole — clearOddsSuspended ripulirà i flag all'inizio del retry.
          await this.markOddSuspended({ outcomeId: err.outcomeId });
        }  else if (err.code === 'ODD_MULTIPLICITY_ERROR' && err.outcomeId) {
          await this.updateOddMultiplicity({
            outcomeId: err.outcomeId,
            multiplicityMin: err.multiplicityMin,
            multiplicityMax: err.multiplicityMax,
          });
          needsRefresh = true;
        } else if (err.code === 'CHANGED_ODD_VALUE' && err.outcomeId) {
          await this.markOddChanged({ outcomeId: err.outcomeId, newOdd: err.newOdd, odd: err.odd });
          this.warningOddsChanged = true;
          needsRefresh = true;
        }
      }
      if (needsRefresh) await this.refreshCart();
    },

    _buildAcceptancePayload(code) {
      const { type, _multiple_detail, _integral_detail, _system_detail, odds } = this.getBet;
      let amount = 0, oddsTotal = 0, winning = 0, winningTotal = 0, bonus = 0, bonusPercentage = 0;

      if (type === 'single' || type === 'multiple') {
        amount          = _multiple_detail.amount;
        oddsTotal       = _multiple_detail.odds_total;
        winning         = _multiple_detail.winning;
        winningTotal    = _multiple_detail.winning_total;
        bonus           = _multiple_detail.bonus;
        bonusPercentage = _multiple_detail.bonus_percentage;
      } else if (type === 'integral') {
        amount       = _integral_detail.amount_total;
        oddsTotal    = _integral_detail.odds_total_max;
        winning      = _integral_detail.winning_max;
        winningTotal = _integral_detail.winning_total_max;
      } else if (type === 'system') {
        amount       = _system_detail.amount_system;
        winning      = _system_detail.winning_max;
        winningTotal = _system_detail.winning_total_max;
      }

      return {
        code,
        type,
        amount,
        odds_total:       oddsTotal,
        winning,
        winning_total:    winningTotal,
        bonus,
        bonus_percentage: bonusPercentage,
        odds:             JSON.parse(JSON.stringify(odds)),
      };
    },

    _showError(code, reason) {
      this.error_code = code;
      this.error_reason = reason;
      this.dialogError = true;
      this._clearErrorTimer();
      this._errorTimer = setTimeout(() => { this.dialogError = false; }, 10000);
    },

    _clearErrorTimer() {
      if (this._errorTimer) { clearTimeout(this._errorTimer); this._errorTimer = null; }
    },

    closeError() {
      this._clearErrorTimer();
      this.dialogError = false;
    },

    _onCleanCart() {
      this.dialogError = false;
      this.warningOddsChanged = false;
      this.showAlertLogin = false;
    },
  },

  mounted() {
    EventBus.$on('cleanTheCart', this._onCleanCart);
  },
  beforeDestroy() {
    this._clearErrorTimer();
    EventBus.$off('cleanTheCart', this._onCleanCart);
  },
};
</script>

<style scoped>
/* ── Root ────────────────────────────────────────────────────────────────── */
.actions-american {
  display: flex;
  flex-direction: column;
}
/* Inline/bar mode: fills parent container completely */
.actions-american--inline {
  height: 100%;
  width: 100%;
}
.actions-american--inline .il-btn-wrap {
  height: 100%;
}
.actions-american--inline .il-place {
  height: 100%;
  border-radius: 0;
}

/* ── Inline alerts ───────────────────────────────────────────────────────── */
.il-alert {
  font-size: var(--font-size-2xl);
  font-weight: 600;
  padding: 5px 10px;
  text-align: center;
}
.il-alert--warn { color: #fbbf24; background: rgba(245,158,11,0.12); }
.il-alert--err  { color: #fca5a5; background: rgba(239,68,68,0.1); }

/* ── Button wrapper ──────────────────────────────────────────────────────── */
.il-btn-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* ── Place bet button (Illinois: rounded text-sm h-14 w-full primary bg) ── */
/* Illinois: h-14 = 56px, bg-illinois-primary, text-illinois-text (#fff) */
.il-place {
  width: 100%;
  height: 56px;
  border: none;
  cursor: pointer;
  border-radius: 4px 4px 0 0;
  overflow: hidden;
  padding: 0;
  display: flex;
  align-items: stretch;
  transition: none;
}
/* Illinois: bg-illinois-primary when active, bg-illinois-border (#444) when disabled */
.il-place__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--color-primary);
  color: #ffffff;
  transition: background 0.3s ease;
}
/* Illinois: hover:bg-illinois-white-20 = rgba(255,255,255,0.2) overlay */
.il-place__hover:hover {
  background: color-mix(in srgb, var(--color-primary) 80%, white 20%);
}
/* Illinois: bg-illinois-border = #444444 when disabled */
.il-place--disabled .il-place__center {
  background: #444444;
  cursor: not-allowed;
}
/* Illinois: confirm changes keeps primary bg */
.il-place--confirm .il-place__center {
  background: var(--color-primary);
}
/* Illinois: text-base font-bold = 15px 700 */
.il-place__row1 {
  font-size: var(--font-size-5xl);
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
}
/* Illinois: text-xs font-medium = 11px 500 */
.il-place__row2 {
  font-size: var(--font-size-xl);
  font-weight: 500;
  color: #ffffff;
  line-height: 1.2;
}

/* ── Book bet button (Illinois: h-10 w-full text-illinois-bg font-bold) ─── */
/* Illinois: h-10 = 40px, full width, below place bet, text color = bg color */
.il-book {
  width: 100%;
  height: 40px;
  border: none;
  cursor: pointer;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 4px 4px;
  transition: color 0.15s;
}
.il-book__label {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  color: var(--text-op-50-platform, rgba(255,255,255,0.5));
}
.il-book:hover .il-book__label {
  color: var(--text-platform, #ffffff);
}

/* ── Spinner ─────────────────────────────────────────────────────────────── */
.il-spin { animation: il-spin 0.8s linear infinite; }
@keyframes il-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ── Error toast (fixed top-right) ──────────────────────────────────────── */
.error-toast {
  position: fixed;
  top: 16px; right: 16px; z-index: 9999;
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  min-width: 260px; max-width: min(380px, calc(100vw - 32px));
  background: var(--bg-card);
  border: 1px solid rgba(239,68,68,0.4);
  border-left: 3px solid #ef4444;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
}
.error-toast__content { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.error-toast__code { font-size: var(--font-size-2xl); font-weight: 600; color: var(--text-platform); }
.error-toast__reason { font-size: var(--font-size-xl); color: var(--text-op-50-platform); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.error-toast__close { background: none; border: none; cursor: pointer; padding: 0; display: flex; color: var(--text-op-50-platform); flex-shrink: 0; }
.error-toast__close:hover { color: var(--text-platform); }
.toast-slide-enter-active, .toast-slide-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.toast-slide-enter, .toast-slide-leave-to { opacity: 0; transform: translateY(-10px); }
@media (max-width: 600px) {
  .error-toast { top: 16px; left: 16px; right: 16px; min-width: 0; max-width: none; }
}
</style>
